import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

function ScriptEditor() {
  const { path: urlPath } = useParams();
  const [scripts, setScripts] = useState([]);
  const [currentScript, setCurrentScript] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});

  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const data = await api.get('/scripts');
        setScripts(data);
        if (urlPath) {
          const scriptData = await api.get(`/scripts/${urlPath}`);
          setCurrentScript(scriptData);
          setContent(scriptData.content);
        }
      } catch (err) {
        console.error('Failed to fetch scripts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScripts();
  }, [urlPath]);

  const handleSelectScript = async (scriptPath) => {
    try {
      const data = await api.get(`/scripts/${scriptPath}`);
      setCurrentScript(data);
      setContent(data.content);
    } catch (err) {
      console.error('Failed to load script:', err);
    }
  };

  const handleSave = async () => {
    if (!currentScript) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.put(`/scripts/${currentScript.path}`, { content });
      setMessage({ type: 'success', text: 'Script saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save script.' });
    } finally {
      setSaving(false);
    }
  };

  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderPath]: !prev[folderPath],
    }));
  };

  const renderTree = (items, parentPath = '') => {
    return items.map((item, idx) => {
      const fullPath = parentPath ? `${parentPath}/${item.name}` : item.name;
      if (item.type === 'folder') {
        const isExpanded = expandedFolders[fullPath];
        return (
          <div key={idx}>
            <div
              className="tree-item folder"
              onClick={() => toggleFolder(fullPath)}
            >
              {isExpanded ? '📂' : '📁'} {item.name}
            </div>
            {isExpanded && (
              <div className="tree-children">
                {renderTree(item.children || [], fullPath)}
              </div>
            )}
          </div>
        );
      }
      return (
        <div
          key={idx}
          className={`tree-item file ${currentScript?.path === fullPath ? 'active' : ''}`}
          onClick={() => handleSelectScript(fullPath)}
        >
          📄 {item.name}
        </div>
      );
    });
  };

  if (loading) return <div className="card">Loading scripts...</div>;

  return (
    <div>
      <div className="page-header">
        <h2>Script Editor</h2>
        <p>View and edit L2Walker bot scripts</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      <div className="two-col">
        <div className="card">
          <div className="card-title">Script Files</div>
          <div className="script-tree">
            {renderTree(scripts)}
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            {currentScript ? `Editing: ${currentScript.name}` : 'Select a script to edit'}
          </div>
          {currentScript && (
            <>
              <div className="form-group">
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  spellCheck={false}
                />
              </div>
              <div className="btn-group">
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Script'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScriptEditor;
